import React, {
    useState,
    useRef,
    useEffect
} from "react";
import {
    fetchData,
    fetchBotResponse,
    sendSMS, sendEmail,
    fetchBuildingByAddress,
    fetchBuildingByBIN,
    fetchBuildingDevices,
    addBuildingAndDevices,
    rewriteConvo,
    createInspectionsNotification
} from "./api";
import "./chatbot.css"; // Import the CSS for animations

import { useNavigate } from "react-router-dom";
import ElliePopUp from "../ElliePopUp";
import { useAuth } from "../../../contexts/AuthContext";
import EllieImage from "./Ellie.png"; // Import Ellie's image


const Chatbot = () => {
    const [chatMessages, setChatMessages] = useState([
        { sender: "bot", text: "Hello! I am Ellie, your assistant for Site Service Cloud. Choose an option:\n\n 1️⃣ Help with SSC\n2️⃣ Send SMS\n3️⃣ Send Email\n4️⃣ Get Building by Address\n5️⃣ Get Building by BIN\n6️⃣ Fetch Data\n7️⃣ Add Account\n8️⃣ Create Inspection Notifications" }
    ]);
    const [chatInput, setChatInput] = useState("");
    const [isVisible, setIsVisible] = useState(false);
    const [isBotTyping, setIsBotTyping] = useState(false);
    const [chatFlow, setChatFlow] = useState(null);
    const [flowLevel, setFlowLevel] = useState(0);
    const [userInputs, setUserInputs] = useState({});
    const [newBuildingFetched, setNewBuildingFetched] = useState(null);
    const [newBIN, setNewBIN] = useState(null);
    const [newBuildingDevices, setNewBuildingDevices] = useState(null);
    const chatContainerRef = useRef(null);
    const navigate = useNavigate();
    const recognitionRef = useRef(null);
    const [isListening, setIsListening] = useState(false);
    const [helpChatHistory, setHelpChatHistory] = useState([]);
    const [popupVisible, setPopupVisible] = useState(true);
    const { user } = useAuth();
    const userRole = user?.role;

    const closePopUp = () => {
        setPopupVisible(false);
    }


    useEffect(() => {
        if (popupVisible && isVisible) {
            closePopUp();
        }
    }, [isVisible])

    useEffect(() => {
        // Check for browser support
        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
        if (!SpeechRecognition) {
            console.warn("Speech Recognition not supported in this browser.");
            return;
        }

        const recognition = new SpeechRecognition();
        recognition.lang = "en-US";
        recognition.interimResults = false;
        recognition.maxAlternatives = 1;

        recognition.onresult = (event) => {
            const transcript = event.results[0][0].transcript.trim();
            if (transcript.endsWith(" ") || transcript.endsWith(".")) {
                transcript = transcript.slice(0, -1);
            }
            setChatInput(prev => prev + transcript);
        };

        recognition.onend = () => {
            setIsListening(false);
        };

        recognition.onerror = (event) => {
            console.error("Speech recognition error:", event.error);
            setIsListening(false);
        };

        recognitionRef.current = recognition;
    }, [setChatInput]);

    const toggleListening = () => {
        if (!recognitionRef.current) return;

        if (isListening) {
            recognitionRef.current.stop();
        } else {
            recognitionRef.current.start();
            setIsListening(true);
        }
    };


    useEffect(() => {
        if (chatContainerRef.current) {
            chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
        }
    }, [chatMessages, isBotTyping]);


    const toggleChatbot = () => setIsVisible((prev) => !prev);

    const resetFlow = () => {

        if (!chatFlow) return;
        setChatMessages((prev) => [...prev, { sender: "bot", text: "Choose an option:\n\n 1️⃣ Help with SSC\n2️⃣ Send SMS\n3️⃣ Send Email\n4️⃣ Get Building by Address\n5️⃣ Get Building by BIN \n6️⃣ Fetch Data\n7️⃣ Add Account\n8️⃣ Create Inspection Notifications" }]);
        setChatFlow(null);
        setFlowLevel(0);
        setUserInputs({});
        setChatInput("");
    };

    const handleUserChoice = (input) => {
        input = input.trim().toLowerCase();
        switch (input) {
            case "1": return "help";
            case "help with ssc": return "help";
            case "help": return "help";
            case "hello": return "help";
            case "hi": return "help";
            case "hey": return "help";
            case "ellie": return "help";
            case "ellievator": return "help";
            case "ellie-vator": return "help";
            case "hey ellie": return "help";
            case "hey ellievator": return "help";
            case "hey ellie-vator": return "help";
            case "2": return "sms";
            case "send sms": return "sms";
            case "sms": return "sms";
            case "3": return "email";
            case "send email": return "email";
            case "send e-mail": return "email";
            case "email": return "email";
            case "e-mail": return "email";
            case "4": return "building_address";
            case "get building by address": return "building_address";
            case "address": return "building_address";
            case "5": return "building_bin";
            case "get building by bin": return "building_bin";
            case "bin": return "building_bin";
            case "6": return "fetch_data";
            case "fetch data": return "fetch_data";
            case "fetch": return "fetch_data";
            case "data": return "fetch_data";
            case "7": return "add_account";
            case "add account": return "add_account";
            case "add": return "add_account";
            case "account": return "add_account";
            case "8": return "notification";
            case "create notification": return "notification";
            case "create notifications": return "notification";
            case "send notification": return "notification";
            case "send notifications": return "notification";
            case "notification": return "notification";
            case "notifications": return "notification";
            case "create inspection notification": return "notification";
            case "create inspection notifications": return "notification";
            case "create inspections notification": return "notification";
            case "create inspections notifications": return "notification";
            case "send inspection notification": return "notification";
            case "send inspection notifications": return "notification";
            case "inspection notification": return "notification";
            case "inspection notifications": return "notification";



            default: return null;
        }
    };

    const sendMessage = async () => {
        if (!chatInput.trim() || isBotTyping) return;

        setChatInput((prev) => prev.trim());

        const userMessage = { sender: "user", text: chatInput };
        setChatMessages((prev) => [...prev, userMessage]);

        let newFlow = chatFlow;
        let newInputs = { ...userInputs };


        if (!chatFlow) {
            newFlow = handleUserChoice(chatInput);
            if (!newFlow) {
                setChatMessages((prev) => [...prev, { sender: "bot", text: "❌ Invalid choice! Please select a valid number:\n\n1️⃣ Help with SSC\n2️⃣ Send SMS\n3️⃣ Send Email\n4️⃣ Get Building by Address\n5️⃣ Get Building by BIN\n6️⃣ Fetch Data\n7️⃣ Add Account\n8️⃣ Create Inspection Notifications" }]);
                setChatInput("");
                return;
            }
            setChatFlow(newFlow);

        }

        if (chatInput.trim().toLowerCase() === "x" || chatInput.trim().toLowerCase() === "cancel") {
            console.log("User canceled the operation");
            setChatMessages((prev) => [...prev, { sender: "bot", text: "❌ Operation canceled." }]);
            resetFlow();
            return;
        }

        while (chatInput.endsWith(".") || chatInput.endsWith(" ")) {
            setChatInput(chatInput.slice(0, -1).trim());
        }

        switch (newFlow) {
            case "nav":
                navigate("/dashboard");
                break;
            case "help":
                if (flowLevel === 0) {
                    setChatMessages((prev) => [...prev, { sender: "bot", text: "🤖 I am Ellie, your assistant for Site Service Cloud. How can I help you? Enter X to cancel anytime ❌" }]);
                    setFlowLevel((prev) => prev + 1);
                }
                else {

                    let convo = rewriteConvo([...helpChatHistory, { sender: "user", text: chatInput }]);
                    let response = await fetchBotResponse(convo);
                    setChatMessages((prev) => [...prev, {
                        sender: "bot", text: response
                        // + "\n\n\nCan I help you further, ask any query? If not, Enter X to cancel ❌" 
                    }]);
                    setHelpChatHistory((prev) => [...prev, { sender: "user", text: chatInput }]);
                    setHelpChatHistory((prev) => [...prev, { sender: "bot", text: response }]);
                    // setFlowLevel((prev) => prev - 1);
                }
                break;


            case "sms":

                if (flowLevel === 0) {
                    setChatMessages((prev) => [...prev, { sender: "bot", text: "📩 Enter the message you want to send or enter X to cancel ❌" }]);
                } else if (flowLevel === 1) {
                    newInputs.message = chatInput;
                    setChatMessages((prev) => [...prev, { sender: "bot", text: "📩 Enter the phone you want to send it to or enter X to cancel ❌" }]);
                } else if (flowLevel === 2) {
                    newInputs.phone = chatInput;
                    setChatMessages((prev) => [...prev, { sender: "bot", text: `📲 Confirm sending SMS to ${newInputs.phone} with message: "${newInputs.message}"?\n1️⃣ Confirm\n2️⃣ Cancel` }])
                } else if (flowLevel === 3) {
                    if (["1", "yes", "confirm", "y", "ok", "okay", "yes", "yes please", "sure", "send"].includes(chatInput.toLowerCase())) {
                        setIsBotTyping(true);
                        let response = await sendSMS(newInputs.phone, newInputs.message);
                        setIsBotTyping(false);
                        setChatMessages((prev) => [...prev, { sender: "bot", text: "✅ SMS sent successfully! " + response }]);
                    } else {
                        setChatMessages((prev) => [...prev, { sender: "bot", text: "❌ SMS request canceled." }]);
                    }
                    resetFlow();
                    return;
                }
                setFlowLevel((prev) => prev + 1);
                break;

            case "email":
                if (flowLevel === 0) {
                    setChatMessages((prev) => [...prev, { sender: "bot", text: "📨 Choose email type:\n0️⃣ Contract\n1️⃣ Work\n2️⃣ Inspection or enter X to cancel ❌" }]);
                } else if (flowLevel === 1) {
                    if (!["0", "1", "2", "contract", "work", "inspection"].includes(chatInput.toLowerCase())) {
                        setChatMessages((prev) => [...prev, { sender: "bot", text: "❌ Invalid choice! 📨 Choose email type:\n0️⃣ Contract\n1️⃣ Work\n2️⃣ Inspection or enter X to cancel ❌" }]);
                        setFlowLevel((prev) => prev - 1);
                    } else {
                        newInputs.emailType = chatInput;
                        setChatMessages((prev) => [...prev, { sender: "bot", text: `📩 Enter the email you want to send it to or enter X to cancel ❌` }]);
                    }
                } else if (flowLevel === 2) {
                    newInputs.email = chatInput;
                    setChatMessages((prev) => [...prev, { sender: "bot", text: `📲 Confirm sending Email to ${newInputs.email} with type ${newInputs.emailType}?\n1️⃣ Confirm\n2️⃣ Cancel` }])
                } else if (flowLevel === 3) {
                    if (["1", "yes", "confirm", "y", "ok", "okay", "yes please", "sure", "send"].includes(chatInput.toLowerCase())) {
                        setIsBotTyping(true);
                        await sendEmail(newInputs.email, newInputs.emailType);
                        setIsBotTyping(false);
                        setChatMessages((prev) => [...prev, { sender: "bot", text: "✅ Email sent successfully!" }]);
                    }
                    else {
                        setChatMessages((prev) => [...prev, { sender: "bot", text: "❌ Email request canceled." }]);
                    }
                    resetFlow();
                    return;
                }
                setFlowLevel((prev) => prev + 1);
                break;

            case "building_bin":
                if (flowLevel === 0) {
                    setChatMessages((prev) => [...prev, { sender: "bot", text: "🏢 Enter the 7-digit BIN number of the building or enter X to cancel ❌" }]);
                } else if (flowLevel === 1) {

                    if (chatInput.length !== 7) {
                        setChatMessages((prev) => [...prev, { sender: "bot", text: "❌ Invalid BIN number! Please enter a 7-digit BIN number  or enter X to cancel ❌" }]);
                        setFlowLevel((prev) => prev - 1);
                    } else {

                        newInputs.bin = chatInput;
                        setIsBotTyping(true);
                        setChatInput("");
                        let { text, BIN, response } = await fetchBuildingByBIN(newInputs.bin);

                        if (BIN) {
                            setNewBIN(BIN);
                        } else {
                            resetFlow();
                            return;
                        }

                        if (response) {
                            setNewBuildingFetched(response);
                            setNewBuildingDevices(null);
                        }

                        setIsBotTyping(false);
                        setChatMessages((prev) => [...prev, { sender: "bot", text: text }]);
                        setChatMessages((prev) => [...prev, { sender: "bot", text: "Do you want to fetch its devices?\n1️⃣ Yes\n2️⃣ No" }]);
                    }
                }
                else if (flowLevel === 2) {

                    if (["1", "yes", "confirm", "y", "ok", "okay", "yes please", "sure", "send"].includes(chatInput.toLowerCase())) {
                        setIsBotTyping(true);
                        setChatInput("");
                        let { text, devices } = await fetchBuildingDevices(newBIN);
                        setNewBuildingDevices(devices);
                        setIsBotTyping(false);
                        setChatMessages((prev) => [...prev, { sender: "bot", text: text }]);
                        setChatMessages((prev) => [...prev, { sender: "bot", text: "Do you want to add the building and it's devices to the database?\n1️⃣ Yes\n2️⃣ No" }]);
                    } else {
                        setChatMessages((prev) => [...prev, { sender: "bot", text: "❌ Building devices request canceled." }]);
                        resetFlow();
                        return;
                    }

                } else if (flowLevel === 3) {

                    if (["1", "yes", "confirm", "y", "ok", "okay", "yes please", "sure", "send"].includes(chatInput.toLowerCase())) {
                        setIsBotTyping(true);
                        setChatInput("");
                        let response = await addBuildingAndDevices(newBuildingFetched, newBuildingDevices);
                        setIsBotTyping(false);
                        setChatMessages((prev) => [...prev, { sender: "bot", text: response }]);
                    } else {
                        setChatMessages((prev) => [...prev, { sender: "bot", text: "❌ Building request canceled." }]);
                    }
                    resetFlow();
                    return;

                }
                setFlowLevel((prev) => prev + 1);
                break;
            case "building_address":
                if (flowLevel === 0) {
                    setChatMessages((prev) => [...prev, { sender: "bot", text: "🏢 Enter the street number of the building or enter X to cancel ❌" }]);
                } else if (flowLevel === 1) {
                    newInputs.streetNum = chatInput;
                    setChatMessages((prev) => [...prev, { sender: "bot", text: "🏢 Enter the street name of the building or enter X to cancel ❌" }]);
                } else if (flowLevel === 2) {
                    newInputs.streetName = chatInput;
                    setChatMessages((prev) => [...prev, { sender: "bot", text: "🏢 Choose the borough:\n 1️⃣ Manhattan \n 2️⃣ Bronx \n 3️⃣ Brooklyn \n 4️⃣ Queens \n5️⃣ Staten Island \n ❌Press  enter X to cancel" }]);
                } else if (flowLevel === 3) {

                    if (!["1", "2", "3", "4", "5", "manhattan", "bronx", "brooklyn", "queens", "staten island"].includes(chatInput.toLowerCase())) {
                        setChatMessages((prev) => [...prev, { sender: "bot", text: "❌ Invalid choice! 🏢 Choose the borough:\n 1️⃣ Manhattan \n 2️⃣ Bronx \n 3️⃣ Brooklyn \n 4️⃣ Queens \n5️⃣ Staten Island \n ❌Press  enter X to cancel" }]);
                        setFlowLevel((prev) => prev - 1);
                    } else {
                        let boroughs = ["Manhattan", "Bronx", "Brooklyn", "Queens", "Staten Island"];
                        let borough;
                        if (isNaN(chatInput)) {
                            borough = chatInput.toLowerCase()
                                .split(' ')
                                .map((s) => s.charAt(0).toUpperCase() + s.substring(1))
                                .join(' ');
                        } else {

                            borough = boroughs[parseInt(chatInput) - 1];
                        }

                        newInputs.borough = borough;
                        setChatMessages((prev) => [...prev, { sender: "bot", text: `🏢 Confirm fetching building at ${newInputs.streetNum} ${newInputs.streetName}, ${newInputs.borough}?\n1️⃣ Confirm\n2️⃣ Cancel` }]);
                    }
                }
                else if (flowLevel === 4) {
                    if (["1", "yes", "confirm", "y", "ok", "okay", "yes please", "sure", "send"].includes(chatInput.toLowerCase())) {
                        setIsBotTyping(true);
                        setChatInput("");
                        let { text, BIN, response } = await fetchBuildingByAddress(newInputs.streetNum, newInputs.streetName, newInputs.borough);
                        setIsBotTyping(false);
                        setChatMessages((prev) => [...prev, { sender: "bot", text: text }]);

                        if (BIN) {
                            setNewBIN(BIN);
                        } else {
                            resetFlow();
                            return;
                        }

                        if (response) {
                            setNewBuildingFetched(response);
                            setNewBuildingDevices(null);
                        }

                        setChatMessages((prev) => [...prev, { sender: "bot", text: "Do you want to fetch its devices?\n1️⃣ Yes\n2️⃣ No" }]);


                    }
                    else {
                        setChatMessages((prev) => [...prev, { sender: "bot", text: "❌ Building request canceled." }]);
                        resetFlow();
                        return;
                    }

                } else if (flowLevel === 5) {

                    if (["1", "yes", "confirm", "y", "ok", "okay", "yes please", "sure", "send"].includes(chatInput.toLowerCase())) {
                        setIsBotTyping(true);
                        setChatInput("");
                        let { text, devices } = await fetchBuildingDevices(newBIN);
                        setNewBuildingDevices(devices);
                        setIsBotTyping(false);
                        setChatMessages((prev) => [...prev, { sender: "bot", text: text }]);
                        setChatMessages((prev) => [...prev, { sender: "bot", text: "Do you want to add the building and it's devices to the database?\n1️⃣ Yes\n2️⃣ No" }]);
                    } else {
                        setChatMessages((prev) => [...prev, { sender: "bot", text: "❌ Building devices request canceled." }]);
                        resetFlow();
                        return;
                    }


                } else if (flowLevel === 6) {

                    if (["1", "yes", "confirm", "y", "ok", "okay", "yes please", "sure", "send"].includes(chatInput.toLowerCase())) {
                        setIsBotTyping(true);
                        setChatInput("");
                        let response = await addBuildingAndDevices(newBuildingFetched, newBuildingDevices);
                        setIsBotTyping(false);
                        setChatMessages((prev) => [...prev, { sender: "bot", text: response }]);
                    } else {
                        setChatMessages((prev) => [...prev, { sender: "bot", text: "❌ Building request canceled." }]);
                    }
                    resetFlow();
                    return;
                }
                setFlowLevel((prev) => prev + 1);
                break;

            case "fetch_data":
                if (flowLevel === 0) {
                    setChatMessages((prev) => [...prev, { sender: "bot", text: "📊 Choose data type:\n0️⃣ Customer Data\n1️⃣ Building Data or enter X to cancel ❌" }])
                }
                else if (flowLevel === 1) {
                    if (!["0", "1", "customer", "building", "customer data", "building data"].includes(chatInput.toLowerCase())) {
                        setChatMessages((prev) => [...prev, { sender: "bot", text: "❌ Invalid choice! 📊 Choose data type:\n0️⃣ Customer Data\n1️⃣ Building Data or enter X to cancel ❌" }]);
                        setFlowLevel((prev) => prev - 1);
                    } else {
                        newInputs.type = chatInput;
                        setChatMessages((prev) => [...prev, { sender: "bot", text: `📊 Enter the ID for type ${newInputs.type} or enter X to cancel ❌` }]);
                    }
                }
                else if (flowLevel === 2) {
                    newInputs.id = chatInput;
                    setIsBotTyping(true);
                    setChatInput("");
                    let response = await fetchData(newInputs.type, newInputs.id);
                    setIsBotTyping(false);
                    setChatMessages((prev) => [...prev, { sender: "bot", text: response }]);
                    resetFlow();
                    return;
                }
                setFlowLevel((prev) => prev + 1);
                break;
            case "add_account":
                if (flowLevel === 0) {
                    setChatMessages((prev) => [...prev, {
                        sender: "bot",
                        text:
                            `👤 To add a new account, you need to add the following:\n\n 1- Agency\n 2- Customer\n 3- Building\n\n Should I help you add these one by one?
                         \n Press  1️⃣ if you want help or anything else to go by yourself.` }])

                } else if (flowLevel === 1) {
                    if (["1", "yes", "confirm", "y", "ok", "okay", "yes please", "sure", "send", "help"].includes(chatInput.toLowerCase())) {
                        navigate("/inspections-violations/agency");
                        setChatMessages((prev) => [...prev, {
                            sender: "bot",
                            text:
                                `👤 We are at the agency module, click on the Add button at the top right section, and add your agency details.\n\n If you already have an existing agency, you can look it up through the list or search it up.
                                Once added (or if alredy existing), you can proceed to the next step. Just tell me the id of the agency so I can keep track for you.` }]);
                    } else {
                        setChatMessages((prev) => [...prev, {
                            sender: "bot",
                            text:
                                `👤 Okay, let me know if you need help with anything else.`
                        }]);
                        resetFlow();
                        return
                    }
                } else if (flowLevel === 2) {
                    newInputs.agencyId = chatInput;
                    navigate("/setup/customers");
                    setChatMessages((prev) => [...prev, {
                        sender: "bot",
                        text:
                            `👤 Great! Now we need to add the customer. Click on the Add button at the top right section, and add your customer details.\n\n If you already have an existing customer, you can look it up through the list or search it up.
                            Once added (or if alredy existing), you can proceed to the next step. Just tell me the id of the customer so I can keep track for you.` }]);
                } else if (flowLevel === 3) {
                    newInputs.customerId = chatInput;
                    navigate("/setup/buildings");
                    setChatMessages((prev) => [...prev, {
                        sender: "bot",
                        text:
                            `👤 Great! Now we need to add the building. Click on the Add button at the top right section, and add your building details.\n\n
                            The agency id is ${newInputs.agencyId} and the customer id is ${newInputs.customerId}. You can use these ids to link the building to the agency and customer.
                            \n \n 
                            I hope this helps! Let me know if you need help with anything else.` }]);
                }


                setFlowLevel((prev) => prev + 1);
                break;

            case "notification":
                if (flowLevel === 0) {
                    setChatMessages((prev) => [
                        ...prev,
                        {
                            sender: "bot",
                            text: `👤 To create a new inspection notification, please specify the jurisdiction/agency from the below list:\n\n1️⃣ NYC DOB`
                        }
                    ]);
                } else if (flowLevel === 1) {
                    if (["1", "nyc dob", "dob", "nyc", "yes", "first"].includes(chatInput.toLowerCase())) {

                        // setUserInputs((prev) => ({ ...prev, agencyId: 1002 }));
                        newInputs.agencyId = 1002;
                        setChatMessages((prev) => [
                            ...prev,
                            {
                                sender: "bot",
                                text: `👤 Great! Now tell the year for the inspections you want it for?`
                            }]);
                    } else {
                        setChatMessages((prev) => [
                            ...prev,
                            {
                                sender: "bot",
                                text: `❌ Invalid choice! Please specify the jurisdiction/agency from the below list:\n\n1️⃣ NYC DOB`
                            }
                        ]);
                        setFlowLevel((prev) => prev - 1);
                    }
                } else if (flowLevel === 2) {


                    if (isNaN(chatInput) || parseInt(chatInput) < new Date().getFullYear()) {
                        setChatMessages((prev) => [
                            ...prev,
                            {
                                sender: "bot",
                                text: `❌ Invalid year! Please enter a valid year for the inspections you want it for.`
                            }
                        ]);
                        setFlowLevel((prev) => prev - 1);
                    } else {
                        // setUserInputs((prev) => ({ ...prev, year: parseInt(chatInput) }));
                        newInputs.year = parseInt(chatInput);
                        setChatMessages((prev) => [
                            ...prev,
                            {
                                sender: "bot",
                                text: `👤 Great! Do you confirm you want to create inspection notification for NYC DOB Buildings for the year ${chatInput}`
                            }
                        ]);
                    }
                }
                else if (flowLevel === 3) {
                    if (["1", "yes", "confirm", "y", "ok", "okay", "yes please", "sure", "send"].includes(chatInput.toLowerCase())) {
                        setIsBotTyping(true);
                        setChatInput("");
                        let response = await createInspectionsNotification(userInputs.agencyId, userInputs.year);
                        setIsBotTyping(false);
                        setChatMessages((prev) => [
                            ...prev,
                            {
                                sender: "bot",
                                text: response
                            }
                        ]);
                    } else {
                        setChatMessages((prev) => [
                            ...prev,
                            {
                                sender: "bot",
                                text: `❌ Inspection notification request canceled.`
                            }
                        ]);
                    }
                    resetFlow();
                    return;
                }
                setFlowLevel((prev) => prev + 1);
                break;

            default:
                setChatMessages((prev) => [...prev, { sender: "bot", text: "I don't understand that request. Please choose a valid option." }]);
        }

        setChatFlow(newFlow);
        setUserInputs(newInputs);
        setChatInput("");
    };

    if (userRole === "APPROVER") {
        return null;
    }


    return (
        <>
            {isVisible && (
                <div className="fixed bottom-24 right-24 w-[500px] h-[600px] bg-white shadow-lg border border-gray-200 rounded-lg z-[9999]">
                    <div className="flex justify-between items-center p-4 bg-blue-500 text-white rounded-t-lg">
                        <h3>Ellie</h3>
                        <button onClick={toggleChatbot} className="text-lg font-bold">✖</button>
                    </div>
                    <div className="p-4 flex flex-col h-[80%] z-[9999]">
                        <div ref={chatContainerRef} className="flex-grow overflow-y-auto border-b border-gray-200 mb-2 chat-container">
                            {chatMessages.map((message, index) => (
                                <div key={index} className={`mb-2 flex ${message.sender === "user" ? "justify-end" : "justify-start"}`}>
                                    {message.sender === "bot" && (
                                        <img
                                            src={EllieImage}
                                            alt="Ellie"
                                            className="w-10 h-10 mr-2 rounded-full"
                                        />
                                    )}
                                    <div className={`p-2 rounded ${message.sender === "user" ? "bg-blue-100 text-right" : "bg-gray-100 text-left"}`}>
                                        {message.sender === "bot"
                                            ? message.text.split("\n").map((line, idx) => <p key={idx}>{line.trim()}</p>)
                                            : message.text.trim()
                                        }
                                    </div>
                                </div>
                            ))}

                            {/* Typing Indicator */}
                            {isBotTyping && (
                                <div className="typing-indicator">
                                    <span></span>
                                    <span></span>
                                    <span></span>
                                </div>
                            )}
                        </div>

                        {/* Input Field */}

                        <div className="chatbot-input flex z-[9999] items-center">
                            <input
                                type="text"
                                value={chatInput}
                                onChange={(e) => setChatInput(e.target.value)}
                                onKeyDown={(e) => e.key === "Enter" && sendMessage()}
                                className="flex-grow border rounded p-2 z-[9999]"
                                placeholder="Type or speak..."
                                readOnly={isBotTyping}
                            />
                            <button
                                onClick={toggleListening}
                                className={`ml-2 px-4 py-2 rounded ${isListening ? "bg-red-500" : "bg-green-500"} text-white hover:opacity-90`}
                                title="Toggle Voice Input"
                            >
                                {isListening ? "Stop" : "🎤"}
                            </button>
                            <button
                                onClick={sendMessage}
                                className="ml-2 bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
                            >
                                Send
                            </button>
                        </div>
                    </div>
                </div >
            )}

            {/* Chatbot Button with a higher z-index */}
            <div className="chatbox fixed bottom-16 right-16 z-[9999]">


                <ElliePopUp onClose={closePopUp} isDisplayed={popupVisible} />


                <button onClick={toggleChatbot} className="fixed bottom-8 right-8 w-24 h-24 bg-blue-500 text-white rounded-full shadow-lg flex items-center justify-center hover:bg-blue-600 text-4xl z-[9999]">
                    💬
                </button>
            </div>
        </>

    );
};

export default Chatbot;
